// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useState } from 'preact/hooks';
import {
  api,
  type SharedVaultState,
  type SharedVaultSummary,
  type SharedVaultRollingLimit,
} from '../api.js';
import { useSession } from '../session-context.js';
import { truncate, formatBtc } from '../format.js';
import '../styles/home.css';
import '../styles/shared-vaults.css';

// ── Pure display helpers (exported for unit tests) ────────────────────

/** Human label for a shared-vault state. Mirrors the yield vault's
 *  status-as-context wording: a short, plain phrase rather than the raw
 *  enum. */
export function sharedVaultStateLabel(state: SharedVaultState): string {
  switch (state) {
    case 'PENDING_INVITES':
      return 'Awaiting consent';
    case 'PROPOSED':
      return 'Awaiting signatures';
    case 'ACTIVE':
      return 'Active';
    case 'EMPTY':
      return 'Empty';
    case 'REJECTED':
      return 'Rejected';
    case 'DELETED':
      return 'Deleted';
  }
}

/** Badge tone for a state — `active` (live funds), `pending` (in setup),
 *  or `inactive` (terminal/empty). Drives the CSS modifier class. */
export function sharedVaultStateTone(
  state: SharedVaultState,
): 'active' | 'pending' | 'inactive' {
  switch (state) {
    case 'ACTIVE':
      return 'active';
    case 'PENDING_INVITES':
    case 'PROPOSED':
      return 'pending';
    case 'EMPTY':
    case 'REJECTED':
    case 'DELETED':
      return 'inactive';
  }
}

/** `2-of-3` quorum descriptor. */
export function quorumLabel(m: number, n: number): string {
  return `${m}-of-${n}`;
}

/** The vault's on-chain keyset size: the `n` human roster keys, plus the
 *  additive verifier key `V` a policy vault folds into its sealed descriptor
 *  (`wsh(sortedmulti(m, humans…, V))`). `V` can help reach the `m` quorum, but
 *  `m` humans always spend without it (R-INV-1) — so it is counted in the keyset
 *  total yet named separately from the human participants, never folded in. */
export function totalKeys(vault: { n: number; has_policy: boolean }): number {
  return vault.n + (vault.has_policy ? 1 : 0);
}

/** The card's participant-count line: the `n` human participants, with a
 *  "+ verifier" tail on a policy vault so the additive verifier key is named
 *  alongside them without being miscounted as a human. */
export function participantSummary(vault: {
  n: number;
  has_policy: boolean;
}): string {
  const base = `${vault.n} participant${vault.n === 1 ? '' : 's'}`;
  return vault.has_policy ? `${base} + verifier` : base;
}

/** Describe a rolling-limit `window_secs` as a human "per …" phrase. Handles
 *  the exact hour/day/week presets the builder offers and falls back to whole
 *  days/hours/seconds for any other window the immutable contract might carry
 *  (A3.1/B3.1 — COB-237/238). */
export function describeLimitWindow(windowSecs: number): string {
  if (windowSecs === 3600) return 'per hour';
  if (windowSecs === 86_400) return 'per day';
  if (windowSecs === 604_800) return 'per week';
  if (windowSecs % 86_400 === 0) return `per ${windowSecs / 86_400} days`;
  if (windowSecs % 3600 === 0) return `per ${windowSecs / 3600} hours`;
  return `per ${windowSecs} seconds`;
}

/** Format a configured rolling limit for display, e.g. "0.05000000 BTC per
 *  day" — the same 8-dp BTC formatting used across the wallet. */
export function formatRollingLimit(limit: SharedVaultRollingLimit): string {
  return `${formatBtc(limit.amount_sats)} BTC ${describeLimitWindow(limit.window_secs)}`;
}

// ── List filter (COB-486 §4) ──────────────────────────────────────────

/** The fields a filter predicate reads. Structural rather than the whole
 *  {@link SharedVaultSummary} — same narrow-input idiom as `totalKeys` /
 *  `participantSummary` above, so the buckets are testable from a two-field
 *  fixture. */
export type SharedVaultFilterInput = Pick<
  SharedVaultSummary,
  'state' | 'my_action_required'
>;

export type SharedVaultFilterKey =
  | 'all'
  | 'needs_me'
  | 'setup'
  | 'active'
  | 'inactive';

export interface SharedVaultFilter {
  key: SharedVaultFilterKey;
  label: string;
  match: (vault: SharedVaultFilterInput) => boolean;
}

/** The chip-row buckets, in render order. A shared vault is never removed from
 *  the caller's list — REJECTED and DELETED rows are soft-deleted and stay
 *  visible forever — so without this the dead rows eventually bury the live
 *  ones.
 *
 *  The buckets deliberately OVERLAP: "Needs me" cuts across state (an unsigned
 *  activation is PROPOSED, an unsigned withdrawal is ACTIVE), so a vault can be
 *  counted in both it and its state bucket. This is a view filter, not a
 *  partition — the counts are per-chip totals, and they do not sum to "All".
 *
 *  `inactive` mirrors the `sharedVaultStateTone` inactive bucket exactly
 *  (EMPTY / REJECTED / DELETED); keep the two in step if a state is added. */
export const SHARED_VAULT_FILTERS: readonly SharedVaultFilter[] = [
  { key: 'all', label: 'All', match: () => true },
  {
    key: 'needs_me',
    label: 'Needs me',
    // Same predicate as the card's `attention` highlight — the chip filters to
    // exactly the set of rows the list already marks. COB-543 dropped the
    // pending-invite arm: pre-claim there is no membership row to list against,
    // so an unclaimed slot never appears on anyone's list — the claim link IS
    // the visibility, and the claim page is where it is answered.
    match: (v) => v.my_action_required,
  },
  {
    key: 'setup',
    label: 'Awaiting setup',
    match: (v) => v.state === 'PENDING_INVITES' || v.state === 'PROPOSED',
  },
  { key: 'active', label: 'Active', match: (v) => v.state === 'ACTIVE' },
  {
    key: 'inactive',
    label: 'Inactive',
    match: (v) => sharedVaultStateTone(v.state) === 'inactive',
  },
];

/** Resolve a filter key to its definition, falling back to `all`. Keeps the
 *  render path free of a non-null assertion on the `find`. */
export function sharedVaultFilterFor(
  key: SharedVaultFilterKey,
): SharedVaultFilter {
  return (
    SHARED_VAULT_FILTERS.find((f) => f.key === key) ?? SHARED_VAULT_FILTERS[0]
  );
}

// ── List page ─────────────────────────────────────────────────────────

export function SharedVaults() {
  const session = useSession();

  if (!session.isAuthenticated) {
    return (
      <div class="vaults-page">
        <h1 class="vaults-page-title">Shared vaults</h1>
        <p class="vault-list-empty">Log in to view your shared vaults.</p>
      </div>
    );
  }

  return (
    <div class="vaults-page">
      <div class="shared-vaults-header">
        <h1 class="vaults-page-title">Shared vaults</h1>
        <a class="shared-vaults-create-cta" href="/shared-vaults/create">
          New shared vault
        </a>
      </div>
      <SharedVaultListSection />
    </div>
  );
}

export function SharedVaultListSection() {
  const session = useSession();
  const [vaults, setVaults] = useState<SharedVaultSummary[] | null>(null);
  // COB-486 §4: single-select chip filter, defaulting to the unfiltered view
  // so nothing is hidden until the user asks for it.
  const [filterKey, setFilterKey] = useState<SharedVaultFilterKey>('all');

  useEffect(() => {
    if (!session.isAuthenticated) {
      setVaults(null);
      return;
    }
    api
      .listSharedVaults()
      .then(setVaults)
      .catch(() => {
        // Leave the list empty on fetch failure — a 401 will already have
        // transitioned the session to `expired` via the api.ts hook.
        setVaults([]);
      });
  }, [session.isAuthenticated]);

  if (vaults === null) {
    return <div class="vault-list-empty">Loading…</div>;
  }

  if (vaults.length === 0) {
    return (
      <div class="vault-list-empty">
        <p>
          No shared vaults yet — create one and invite co-signers to get
          started.
        </p>
      </div>
    );
  }

  // Past the empty-list early return above, so the chip row only ever renders
  // over a non-empty list.
  const visible = vaults.filter(sharedVaultFilterFor(filterKey).match);

  return (
    <div class="vault-list">
      <div class="sv-filter-chips" role="group" aria-label="Filter shared vaults">
        {SHARED_VAULT_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            class="sv-filter-chip"
            aria-pressed={f.key === filterKey}
            onClick={() => setFilterKey(f.key)}
          >
            {f.label} ({vaults.filter(f.match).length})
          </button>
        ))}
      </div>
      {visible.map((v) => (
        <SharedVaultCard key={v.vault_id} vault={v} />
      ))}
      {visible.length === 0 && (
        <p class="vault-list-empty-inline">
          No shared vaults match this filter.
        </p>
      )}
    </div>
  );
}

export function SharedVaultCard({ vault }: { vault: SharedVaultSummary }) {
  const tone = sharedVaultStateTone(vault.state);
  // COB-543: `my_action_required` is now the whole of it. A vault only reaches
  // this list once the caller holds a claimed slot in it, so there is no
  // pending-invite state left to highlight here.
  const attention = vault.my_action_required;
  return (
    <a
      class={`vault-card shared-vault-card${
        attention ? ' vault-card--attention' : ''
      }`}
      href={`/shared-vault/${vault.vault_id}`}
    >
      <div class="vault-card-header">
        <span class={`shared-vault-badge shared-vault-badge--${tone}`}>
          {sharedVaultStateLabel(vault.state)}
        </span>
        {vault.name ? (
          <span class="shared-vault-card-name">{vault.name}</span>
        ) : (
          <span class="vault-card-id mono">{truncate(vault.vault_id, 12)}</span>
        )}
      </div>
      <div class="shared-vault-card-meta">
        <span class="shared-vault-quorum">
          {quorumLabel(vault.quorum_m, totalKeys(vault))}
        </span>
        <span class="shared-vault-participants">
          {participantSummary(vault)}
        </span>
      </div>
      {vault.my_action_required && (
        <div class="shared-vault-pill">Pending your signature</div>
      )}
    </a>
  );
}
